import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, Calendar, Clock, ArrowRight, BookOpen, TrendingUp, AlertCircle, DollarSign, Loader } from 'lucide-react';
import Header from '../components/Header';
import { publicApi } from '../utils/api';
import { setPageSeo, resetPageSeo } from '../utils/seo';

const categoryIcons = {
  'Education': BookOpen,
  'News': TrendingUp,
  'Tips': AlertCircle,
  'Tips & Guides': AlertCircle,
  'Analysis': DollarSign,
  'Rate Updates': TrendingUp,
  'default': BookOpen
};

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPosts();
    setPageSeo({
      title: 'Mortgage Insights & Renewal Guides | My Mortgage Canada',
      description: 'Expert guides on Canadian mortgage renewal, rates, payment shock, negotiation, and home equity strategies. Updated with live market data.',
      canonical: `${window.location.origin}/blog`,
    });
    return () => resetPageSeo();
  }, []);

  const fetchPosts = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use publicApi for unauthenticated requests - no credentials needed
      const response = await publicApi.get(`/api/blog/posts?published_only=true&_t=${Date.now()}`);
      setPosts(response.data || []);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
      setError('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-CA', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const featuredPosts = posts.filter(post => post.featured);
  const regularPosts = posts.filter(post => !post.featured);
  
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      
      {/* Hero Section */}
      <div className="pt-24 pb-12 bg-gradient-to-b from-primary/5 to-background">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              Mortgage <span className="text-primary">Insights</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Stay informed with the latest Canadian mortgage news, rate updates, 
              and expert guidance for your renewal journey.
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="max-w-7xl mx-auto px-6 py-12 flex justify-center">
          <Loader className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="max-w-7xl mx-auto px-6 py-12 text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button 
            onClick={fetchPosts}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg"
          >
            Try Again
          </button>
        </div>
      ) : posts.length === 0 ? (
        <div className="max-w-7xl mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">No blog posts yet. Check back soon!</p>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-6 py-12">
          {/* Featured Posts */}
          {featuredPosts.length > 0 && (
            <>
              <h2 className="text-2xl font-heading font-semibold mb-6">Featured Articles</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                {featuredPosts.map((post) => {
                  const CategoryIcon = categoryIcons[post.category] || categoryIcons.default;
                  return (
                    <Link
                      key={post.post_id}
                      to={`/blog/${post.slug}`}
                      className="group bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/50 transition-all"
                    >
                      {post.image_url && (
                        <div className="aspect-video overflow-hidden">
                          <img 
                            src={post.image_url} 
                            alt={post.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                            <CategoryIcon className="h-3 w-3" />
                            {post.category}
                          </span>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(post.created_at)}
                          </span>
                        </div>
                        <h3 className="text-xl font-heading font-semibold mb-2 group-hover:text-primary transition-colors">
                          {post.title}
                        </h3>
                        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {post.read_time || '5 min read'}
                          </span>
                          <span className="text-primary text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                            Read More
                            <ArrowRight className="h-4 w-4" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </>
          )}
          
          {/* Regular Posts */}
          {regularPosts.length > 0 && (
            <>
              <h2 className="text-2xl font-heading font-semibold mb-6">
                {featuredPosts.length > 0 ? 'Latest Articles' : 'All Articles'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {regularPosts.map((post) => {
                  const CategoryIcon = categoryIcons[post.category] || categoryIcons.default;
                  return (
                    <Link
                      key={post.post_id}
                      to={`/blog/${post.slug}`}
                      className="group bg-card border border-border rounded-xl overflow-hidden hover:border-primary/50 transition-all"
                    >
                      {post.image_url && (
                        <div className="aspect-video overflow-hidden">
                          <img 
                            src={post.image_url} 
                            alt={post.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                      )}
                      <div className="p-5">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs font-medium">
                            <CategoryIcon className="h-3 w-3" />
                            {post.category}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(post.created_at)}
                          </span>
                        </div>
                        <h3 className="text-lg font-heading font-semibold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                          {post.title}
                        </h3>
                        <p className="text-muted-foreground text-sm mb-3 line-clamp-2">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {post.read_time || '5 min read'}
                          </span>
                          <span className="text-primary text-sm font-medium flex items-center gap-1">
                            Read
                            <ArrowRight className="h-3 w-3" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
      
      {/* Newsletter CTA */}
      <div className="bg-card border-y border-border py-16">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-heading font-semibold mb-3">
            Stay Updated on Mortgage Rates
          </h2>
          <p className="text-muted-foreground mb-6">
            Get weekly insights on Canadian mortgage rates, Bank of Canada decisions, 
            and tips to save on your renewal.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
          >
            Create Free Account
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="border-t border-border bg-background">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <Home className="h-5 w-5 text-primary" />
              <span className="font-heading font-semibold">My Mortgage Canada</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</Link>
              <p className="text-sm text-muted-foreground">
                &copy; 2025 My Mortgage Canada. Empowering Canadian homeowners.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPage;
